// iter150 — Public Privacy Policy page for Twilio A2P 10DLC compliance.
// MUST render publicly with NO authentication redirect (Twilio compliance
// review verifies the URL returns 200 OK without login). Wired into
// App.js at /privacy-policy AND /privacy (both accepted). Also listed
// in RAW_ROUTE_EXACT so admin chrome doesn't wrap it.

const PRIVACY_LAST_UPDATED = "February 28, 2026";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="privacy-policy-page">
      <header className="border-b border-gray-100">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2" data-testid="privacy-home-link">
            <div className="w-9 h-9 rounded-full bg-[#047857] text-white flex items-center justify-center font-extrabold">J</div>
            <div className="font-extrabold tracking-tight text-gray-900">JDSN Lawn Care</div>
          </a>
          <a href="/" className="text-[13px] font-bold text-[#047857] hover:underline">← Back to home</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6 leading-relaxed">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] font-extrabold text-gray-500">Legal</div>
          <h1 className="mt-1 text-4xl sm:text-5xl font-extrabold tracking-tight font-[Outfit]">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500">Last updated: {PRIVACY_LAST_UPDATED}</p>
        </div>

        <p>
          JDSN Lawn Care (&quot;<strong>JDSN</strong>&quot;, &quot;<strong>we</strong>&quot;, &quot;<strong>us</strong>&quot;, &quot;<strong>our</strong>&quot;)
          respects your privacy. This Privacy Policy explains what personal information we
          collect, how we use it, and the rights you have. This policy governs use of our
          website <a href="https://jdsnlawncare.com" className="text-[#047857] font-bold">jdsnlawncare.com</a>,
          our booking and quote system, and any SMS / email communications we send you.
        </p>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">1. Information we collect</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Contact information</strong> — name, email address, mobile phone number, service address.</li>
            <li><strong>Property information</strong> — lot size, mowable square footage, and photos of your yard (only when you upload them for a custom quote).</li>
            <li><strong>Service preferences</strong> — chosen services, cadence (weekly, biweekly, etc.), gate width, yard-area selection.</li>
            <li><strong>Payment information</strong> — processed exclusively by Stripe. We store a Stripe Customer ID and Payment Method ID only; we <strong>never</strong> store raw card numbers on our servers.</li>
            <li><strong>Booking and messaging history</strong> — the visits you scheduled, the invoices we generated, and the SMS / email messages we sent you.</li>
            <li><strong>Technical data</strong> — IP address, browser type, device info, and pages visited, used to keep the site secure and improve reliability.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">2. How we use SMS consent</h2>
          <p>
            When you provide your mobile phone number and opt in — either by checking an
            SMS-consent checkbox on our booking form, replying to an SMS request, or
            submitting a service request — you agree to receive text messages from JDSN
            Lawn Care related to your service.
          </p>
          <ul className="list-disc pl-6 space-y-1.5 mt-2">
            <li>Booking confirmations and appointment reminders.</li>
            <li>Crew arrival, completion, and payment receipts.</li>
            <li>Payment failure notices and quote / offer updates.</li>
            <li>Occasional service updates (seasonal reminders, cadence changes you requested).</li>
          </ul>
          <p className="mt-2">
            <strong>Message frequency varies.</strong> Message and data rates may apply.
            Reply <strong>STOP</strong> to any message to opt out of further SMS.
            Reply <strong>HELP</strong> for assistance or contact us at
            <a href="tel:9048328730" className="text-[#047857] font-bold"> (904) 832-8730</a>.
          </p>
        </section>

        <section className="rounded-xl border-2 border-emerald-200 bg-emerald-50/60 p-5">
          <h2 className="text-xl font-extrabold tracking-tight mb-2">3. We do NOT sell or share your mobile information</h2>
          <p>
            <strong>Your mobile phone number and SMS consent are never sold, rented,
            leased, or shared with third parties for their own marketing or
            promotional purposes.</strong> We share phone numbers only with service
            providers strictly necessary to deliver messages to you (for example, our
            SMS gateway Twilio) and only for the purpose of sending you the messages
            you signed up for. This restriction applies regardless of any other
            provision of this policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">4. How we use your information</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Provide, schedule, and complete the lawn-care services you request.</li>
            <li>Send you the confirmations, reminders, receipts, and service updates listed above.</li>
            <li>Process payments via Stripe and issue invoices / receipts.</li>
            <li>Respond to inquiries, requests, and support questions you send us.</li>
            <li>Improve our services, prevent fraud, and comply with legal obligations.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">5. Who we share information with</h2>
          <p>We share the minimum necessary information with:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-1.5">
            <li><strong>Stripe</strong> — payment processing (card details are collected directly by Stripe and never touch our servers).</li>
            <li><strong>Twilio</strong> — SMS delivery to your phone (subject to the restriction in Section 3).</li>
            <li><strong>Google (Gmail SMTP, Maps, OAuth)</strong> — email delivery, address verification, and optional Sign-in-with-Google.</li>
            <li><strong>Cloud hosting providers</strong> — to store your account data securely.</li>
            <li><strong>Law enforcement or regulators</strong> — only when legally required (subpoena, court order).</li>
          </ul>
          <p className="mt-2">We do <strong>not</strong> sell your personal information to anyone.</p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">6. How we protect your data</h2>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>All traffic between your device and our servers is encrypted with TLS / HTTPS.</li>
            <li>Payment card data is handled exclusively by Stripe (PCI-DSS Level&nbsp;1 certified). We store only Stripe Customer / PaymentMethod IDs on our side.</li>
            <li>Passwords are stored as one-way bcrypt hashes; we cannot read your password.</li>
            <li>Access to production systems is restricted to authorised JDSN Lawn Care personnel.</li>
            <li>We retain your data only as long as needed to provide the service, comply with legal obligations, and resolve disputes.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">7. Your rights</h2>
          <p>You can at any time:</p>
          <ul className="list-disc pl-6 space-y-1.5 mt-1.5">
            <li>Opt out of SMS by replying <strong>STOP</strong>.</li>
            <li>Unsubscribe from marketing emails via the &quot;Unsubscribe&quot; link in the footer of any email.</li>
            <li>Request a copy of the personal information we hold about you.</li>
            <li>Request correction or deletion of your personal information (subject to legal retention requirements).</li>
            <li>Close your account by contacting us — see Section&nbsp;9.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">8. Cookies</h2>
          <p>
            We use essential cookies to keep you signed in and to remember your booking
            preferences. We do not use third-party advertising or tracking cookies.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-extrabold tracking-tight mt-6 mb-2">9. Contact us</h2>
          <p>
            JDSN Lawn Care · Jacksonville, Florida<br />
            Phone: <a href="tel:9048328730" className="text-[#047857] font-bold">(904) 832-8730</a><br />
            Email: <a href="mailto:jdsnlawncare@gmail.com" className="text-[#047857] font-bold">jdsnlawncare@gmail.com</a>
          </p>
          <p className="mt-2 text-sm text-gray-600">
            See also our <a href="/terms-and-conditions" className="text-[#047857] font-bold">Terms &amp; Conditions</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-gray-100 mt-8">
        <div className="max-w-3xl mx-auto px-6 py-6 text-xs text-gray-500 flex flex-wrap gap-3 justify-between">
          <span>© {new Date().getFullYear()} JDSN Lawn Care. All rights reserved.</span>
          <span className="flex gap-4">
            <a href="/privacy-policy" className="hover:text-[#047857] font-bold">Privacy Policy</a>
            <a href="/terms-and-conditions" className="hover:text-[#047857] font-bold">Terms &amp; Conditions</a>
          </span>
        </div>
      </footer>
    </div>
  );
}
